// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  deleteUser,
  sendPasswordResetEmail,
  signInWithPopup  // הוסף את זה
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../services/firebase/firebaseConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      
      if (firebaseUser) {
        try {
          // טעינת פרופיל המשתמש מ-Firestore
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const userData = userDoc.exists() ? userDoc.data() : null;
          
          setUser(firebaseUser);
          setUserProfile(userData);
          setIsAuthenticated(true);
          
        
        } catch (error) {
          console.error('❌ Error loading user profile:', error);
          setUser(firebaseUser);
          setUserProfile(null);
          setIsAuthenticated(true);
        }
      } else {
        setUser(null);
        setUserProfile(null);
        setIsAuthenticated(false);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // התחברות משתמש
  const login = async (email, password) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'שגיאה בהתחברות';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'משתמש לא נמצא במערכת';
          break;
        case 'auth/wrong-password':
          errorMessage = 'סיסמה שגויה';
          break;
        case 'auth/invalid-email':
          errorMessage = 'כתובת אימייל לא תקינה';
          break;
        case 'auth/user-disabled':
          errorMessage = 'חשבון המשתמש הושבת';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'יותר מדי ניסיונות התחברות. נסה שוב מאוחר יותר';
          break;
        default:
          errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
// התחברות עם גוגל
// התחברות עם גוגל
const loginWithGoogle = async () => {
  try {
    setLoading(true);
    const result = await signInWithPopup(auth, googleProvider);
    const firebaseUser = result.user;
    
    // בדיקה אם זה משתמש חדש או קיים
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    
    if (!userDoc.exists()) {
      // משתמש חדש - יצירת פרופיל חלקי
      const userProfile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        firstName: firebaseUser.displayName?.split(' ')[0] || '',
        lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
        phone: '', // ריק - יצטרך למלא
        displayName: firebaseUser.displayName || '',
        isAdmin: false,
        favorites: [],
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        profileComplete: false, // לא שלם!
        authProvider: 'google'
      };
      
      await setDoc(doc(db, 'users', firebaseUser.uid), userProfile);
      
      return { 
        success: true, 
        user: firebaseUser,
        isNewUser: true,
        needsProfileCompletion: true // הוסף דגל
      };
    } else {
      // משתמש קיים - בדוק אם הפרופיל שלם
      const userData = userDoc.data();
      const needsCompletion = !userData.profileComplete || 
                             !userData.phone || 
                             !userData.firstName || 
                             !userData.lastName;
      
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        lastLoginAt: new Date().toISOString()
      });
      
      return { 
        success: true, 
        user: firebaseUser,
        isNewUser: false,
        needsProfileCompletion: needsCompletion
      };
    }
    
  }  catch (error) {
    console.error('Google login error:', error);
    let errorMessage = 'שגיאה בהתחברות עם גוגל';
    
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        errorMessage = 'המשתמש סגר את חלון ההתחברות';
        break;
      case 'auth/popup-blocked':
        errorMessage = 'החלון נחסם על ידי הדפדפן';
        break;
      case 'auth/account-exists-with-different-credential':
        errorMessage = 'קיים חשבון עם אימייל זה בשיטת התחברות אחרת';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  } finally {
    setLoading(false);
  }
};
  // הרשמת משתמש חדש
 const register = async (userData) => {
  try {
    setLoading(true);
    const { email, password, firstName, lastName, phone, pw } = userData;
    
    // יצירת משתמש ב-Firebase Auth
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = result.user;
    
    // עדכון שם המשתמש ב-Firebase Auth
    await updateProfile(firebaseUser, {
      displayName: `${firstName} ${lastName}`
    });
    
    // 🆕 שמירת פרטי המשתמש ב-Firestore כולל הסיסמה
    const userProfile = {
      uid: firebaseUser.uid,
      email: email,
      firstName: firstName,
      lastName: lastName,
      phone: phone || '',
      displayName: `${firstName} ${lastName}`,
      isAdmin: false, // ברירת מחדל - לא אדמין
      favorites: [], // רשימת מועדפים ריקה
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      profileComplete: true,
      pw: pw || password // 🆕 שמירת הסיסמה בשדה pw
    };
    
    await setDoc(doc(db, 'users', firebaseUser.uid), userProfile);
    
    console.log('✅ User registered successfully:', email);
    console.log('📝 Password saved in pw field'); // לוג לוודא ששמרנו את הסיסמה
    
    return { success: true, user: firebaseUser };
    
  } catch (error) {
    console.error('Registration error:', error);
    let errorMessage = 'שגיאה בהרשמה';
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'כתובת האימייל כבר בשימוש';
        break;
      case 'auth/invalid-email':
        errorMessage = 'כתובת אימייל לא תקינה';
        break;
      case 'auth/weak-password':
        errorMessage = 'הסיסמה חלשה מדי (לפחות 6 תווים)';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  } finally {
    setLoading(false);
  }
};

  // התנתקות
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // עדכון פרופיל משתמש
  const updateUserProfile = async (updatedData) => {
    try {
      if (!user) throw new Error('לא מחובר למערכת');
      
      setLoading(true);
      
      // עדכון ב-Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...updatedData,
        updatedAt: new Date().toISOString()
      });
      
      // עדכון שם התצוגה ב-Firebase Auth אם נדרש
      if (updatedData.firstName || updatedData.lastName) {
        const displayName = `${updatedData.firstName || userProfile?.firstName} ${updatedData.lastName || userProfile?.lastName}`;
        await updateProfile(user, { displayName });
      }
      
      // רענון הפרופיל המקומי
      const updatedProfile = { ...userProfile, ...updatedData };
      setUserProfile(updatedProfile);
      
      
      return { success: true, profile: updatedProfile };
      
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // מחיקת חשבון משתמש
  const deleteUserAccount = async () => {
    try {
      if (!user) throw new Error('לא מחובר למערכת');
      
      setLoading(true);
      
      // מחיקת נתוני המשתמש מ-Firestore
      await deleteDoc(doc(db, 'users', user.uid));
      
      // מחיקת החשבון מ-Firebase Auth
      await deleteUser(user);
      
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
      
    
      return { success: true };
      
    } catch (error) {
      console.error('Delete account error:', error);
      let errorMessage = 'שגיאה במחיקת החשבון';
      
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'נדרשת התחברות מחדש כדי למחוק את החשבון';
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // איפוס סיסמה
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'נשלח אימייל לאיפוס סיסמה' };
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMessage = 'שגיאה בשליחת אימייל איפוס';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'לא נמצא משתמש עם כתובת אימייל זו';
          break;
        case 'auth/invalid-email':
          errorMessage = 'כתובת אימייל לא תקינה';
          break;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  // הוספת מוצר למועדפים
  const addToFavorites = async (productId, productData) => {
    try {
      if (!user) throw new Error('נדרשת התחברות למערכת');
      
      const userRef = doc(db, 'users', user.uid);
      const currentFavorites = userProfile?.favorites || [];
      
      // בדיקה אם המוצר כבר במועדפים
      if (currentFavorites.some(fav => fav.id === productId)) {
        return { success: false, error: 'המוצר כבר נמצא במועדפים' };
      }
      
      const favorite = {
        id: productId,
        name: productData.name,
        company: productData.company,
        category: productData.category,
        addedAt: new Date().toISOString()
      };
      
      const updatedFavorites = [...currentFavorites, favorite];
      
      await updateDoc(userRef, { favorites: updatedFavorites });
      setUserProfile(prev => ({ ...prev, favorites: updatedFavorites }));
      
      console.log('✅ Added to favorites:', productId);
      return { success: true };
      
    } catch (error) {
      console.error('Add to favorites error:', error);
      return { success: false, error: error.message };
    }
  };

  // הסרת מוצר מהמועדפים
  const removeFromFavorites = async (productId) => {
    try {
      if (!user) throw new Error('נדרשת התחברות למערכת');
      
      const userRef = doc(db, 'users', user.uid);
      const currentFavorites = userProfile?.favorites || [];
      const updatedFavorites = currentFavorites.filter(fav => fav.id !== productId);
      
      await updateDoc(userRef, { favorites: updatedFavorites });
      setUserProfile(prev => ({ ...prev, favorites: updatedFavorites }));
      
      console.log('✅ Removed from favorites:', productId);
      return { success: true };
      
    } catch (error) {
      console.error('Remove from favorites error:', error);
      return { success: false, error: error.message };
    }
  };

  // בדיקה אם מוצר במועדפים
  const isFavorite = (productId) => {
    return userProfile?.favorites?.some(fav => fav.id === productId) || false;
  };

  // קבלת רשימת המועדפים
  const getFavorites = () => {
    return userProfile?.favorites || [];
  };

  // בדיקת הרשאות אדמין
  const isAdmin = () => {
    return userProfile?.isAdmin === true;
  };

  // בדיקת הרשאות כלליות
  const hasPermission = (permission) => {
    if (userProfile?.isAdmin) return true;
    return userProfile?.permissions?.includes(permission) || false;
  };

const value = {
  // משתמש נוכחי
  user,
  userProfile,
  isAuthenticated,
  loading,
  
  // פעולות התחברות
  login,
  loginWithGoogle, // הוסף את השורה הזו
  register,
  logout,
  resetPassword,
  
  // ניהול פרופיל
  updateUserProfile,
  deleteUserAccount,
  
  // מועדפים
  addToFavorites,
  removeFromFavorites,
  isFavorite,
  getFavorites,
  
  // הרשאות
  isAdmin,
  hasPermission
};

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;